// frontend-react/src/services/authService.ts
import { getApiBaseUrl } from '../config/api';
import apiClient from './apiClient';
import type { AxiosError } from 'axios';

// 權限常數
export const PERMISSIONS = {
  CREATE: 1,
  READ: 2,
  UPDATE: 4,
  DELETE: 8,
  MANAGE: 16,
} as const;

export type Role = 'viewer' | 'operator' | 'reviewer' | 'admin';

export interface CompanyInfo {
  id: number;
  name: string;
  code: string;
}

export interface CompanyFeatures {
  faq_enabled: boolean;
  risk_level_enabled: boolean;
  ad_review_enabled: boolean;
  ai_rules_enabled: boolean;
  ai_ruleset_upload_enabled: boolean;
  image_description_enabled: boolean;
  legaltech_library_enabled: boolean;
  ai_rule_optimization_enabled: boolean;
  contract_comparison_enabled: boolean;
}

export const DEFAULT_COMPANY_FEATURES: CompanyFeatures = {
  faq_enabled: true,
  risk_level_enabled: true,
  ad_review_enabled: true,
  ai_rules_enabled: false,
  ai_ruleset_upload_enabled: false,
  image_description_enabled: true,
  legaltech_library_enabled: false,
  ai_rule_optimization_enabled: false,
  contract_comparison_enabled: false,
};

export interface User {
  id: string | number;
  email: string;
  name: string;
  role?: string; // 主要角色
  roles?: string[]; // 多角色字段
  department?: string;
  company?: string;
  company_code?: string;
  is_active: boolean;
  is_department_manager?: boolean;
  is_superadmin?: boolean;
  all_companies?: CompanyInfo[];
  company_features?: CompanyFeatures;
  validation_mode?: boolean;
  preferred_rule_set_id?: number | null;
  permissions?: {
    can_view_rules?: boolean;
    can_upload_rules?: boolean;
    can_edit_rules?: boolean;
    can_manage_users?: boolean;
    can_edit_faq?: boolean;
    can_use_ad_review?: boolean;
    can_use_ai_rules?: boolean;
    can_use_validation_mode?: boolean;
    can_edit_review_cards?: boolean;
    can_use_contract_comparison?: boolean;

    // 舊欄位向後兼容
    can_use_comparison?: boolean;
    can_use_generation?: boolean;
    can_access_all_features?: boolean;
  };
  avatar?: string;
}

export interface AuthResponse {
  user: User;
  token: string;
}

export function getUserRoleDisplay(user: User | null): string {
  if (!user) return 'USER';
  if (user.is_superadmin) return '平台管理員';
  if (user.roles?.includes('ADMIN')) return 'ADMIN';
  if (user.is_department_manager) return `${user.department || ''}主管`;
  return user.roles?.[0]?.toUpperCase() || 'USER';
}

export class PasswordExpiredError extends Error {
  rotationDays?: number;

  constructor(message: string, rotationDays?: number) {
    super(message);
    this.name = 'PasswordExpiredError';

    // ✅ exactOptionalPropertyTypes: true 下，optional 欄位不能「明確被指定 undefined」
    if (rotationDays !== undefined) {
      this.rotationDays = rotationDays;
    }
  }
}

function parsePasswordExpired(body: any): {
  hit: boolean;
  rotationDays?: number;
  message?: string;
} {
  const detail = body?.detail;

  // 後端格式：{"detail":{"error_code":"PASSWORD_EXPIRED","message":"...","rotation_days":90}}
  if (detail && typeof detail === 'object') {
    if (detail.error_code === 'PASSWORD_EXPIRED') {
      return { hit: true, rotationDays: detail.rotation_days, message: detail.message };
    }
  }

  // 兼容：detail 是字串
  if (typeof detail === 'string' && detail.includes('PASSWORD_EXPIRED')) {
    return { hit: true };
  }

  return { hit: false };
}

function extractErrorMessage(err: any, fallback: string): string {
  const detail = err?.response?.data?.detail;

  // detail 可能是 string 或 object
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;

  // 兼容 errorBody?.message
  const msg = err?.response?.data?.message;
  if (typeof msg === 'string' && msg) return msg;

  return fallback;
}

class AuthService {
  private getApiBase(): string {
    return process.env.REACT_APP_API_URL || getApiBaseUrl();
  }

  // ✅ 登入：含 captchaToken
  async login(
    email: string,
    password: string,
    captchaToken?: string,
    force: boolean = false
  ): Promise<AuthResponse> {
    try {
      // ✅ 用 apiClient（axios）讓 401 可被全域 interceptor 統一處理
      const resp = await apiClient.post(
        `${this.getApiBase()}/api/auth/login`,
        {
          email,
          password,
          recaptcha_token: captchaToken,
          device_id: getOrCreateDeviceId(),
          force,
        },
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const data = resp.data;
      return { user: data.user, token: data.token };
    } catch (e: any) {
      const err = e as AxiosError<any>;

      // ✅ Single Active Session: 409 提示（後端回 error_code）
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;

      if (status === 409 && detail?.error_code === 'ACTIVE_SESSION_EXISTS') {
        const ex: any = new Error(detail.message || '此帳號已在其他裝置登入');
        ex.name = 'ActiveSessionExistsError';
        ex.detail = detail; // 讓 LoginPage 可以拿到內容顯示
        throw ex;
      }

      // ✅ 密碼過期：403
      const expired = parsePasswordExpired(err?.response?.data);
      if (status === 403 && expired.hit) {
        throw new PasswordExpiredError(
          expired.message || '密碼已過期，請先更新密碼。',
          expired.rotationDays
        );
      }

      // 其餘錯誤
      throw new Error(extractErrorMessage(err, '登入失敗'));
    }
  }

  // ✅ 註冊：含 captchaToken
  async register(
    email: string,
    password: string,
    name: string,
    captchaToken?: string
  ): Promise<AuthResponse> {
    try {
      const resp = await apiClient.post(
        `${this.getApiBase()}/api/auth/register`,
        {
          email,
          password,
          name,
          recaptcha_token: captchaToken,
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      return resp.data as AuthResponse;
    } catch (e: any) {
      const err = e as AxiosError<any>;

      // ✅ 密碼過期：通常註冊不會遇到，但保持一致
      const expired = parsePasswordExpired(err?.response?.data);
      if (err?.response?.status === 403 && expired.hit) {
        throw new PasswordExpiredError(
          expired.message || '密碼已過期，請先更新密碼。',
          expired.rotationDays
        );
      }

      throw new Error(extractErrorMessage(err, '註冊失敗'));
    }
  }

  // ✅ Google 登入 URL
  getGoogleLoginUrl(): string {
    const redirectUri = `${window.location.origin}/auth/google/callback`;
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      access_type: 'offline',
    });

    return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  }

  // ✅ Google callback
  async handleGoogleCallback(code: string): Promise<AuthResponse> {
    try {
      const resp = await apiClient.post(
        `${this.getApiBase()}/api/auth/google/callback`,
        { code },
        { headers: { 'Content-Type': 'application/json' } }
      );

      return resp.data as AuthResponse;
    } catch (e: any) {
      const err = e as AxiosError<any>;
      throw new Error(extractErrorMessage(err, 'Google 登入失敗'));
    }
  }

  static hasPermission(userPermMask: number, requiredPermission: number): boolean {
    return (userPermMask & requiredPermission) === requiredPermission;
  }

  async logout(): Promise<void> {
    const token = this.getToken();

    // ✅ 用 apiClient，避免 fetch 沒帶 interceptor（但登出本身不重要）
    if (token) {
      await apiClient
        .post(`${this.getApiBase()}/api/auth/logout`, null, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .catch(() => {});
    }

    this.removeToken();
    this.removeUser();
  }

  saveToken(token: string): void {
    localStorage.setItem('auth_token', token);
  }

  getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  removeToken(): void {
    localStorage.removeItem('auth_token');
  }

  saveUser(user: User): void {
    // ✅ 讓 localStorage 永遠存 string id（避免 /login 回 int、/me 回 string 造成型別混亂）
    const normalized: User = { ...user, id: String(user.id) };
    localStorage.setItem('auth_user', JSON.stringify(normalized));
  }

  getUser(): User | null {
    const userData = localStorage.getItem('auth_user');
    return userData ? JSON.parse(userData) : null;
  }

  removeUser(): void {
    localStorage.removeItem('auth_user');
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  async verifyToken(): Promise<boolean> {
    const token = this.getToken();
    if (!token) return false;

    try {
      // ✅ 這條路徑常被用來「驗證是否還有效」：用 apiClient 才能統一 401 行為
      await apiClient.get(`${this.getApiBase()}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return true;
    } catch (e: any) {
      const err = e as AxiosError<any>;
      const status = err?.response?.status;

      const expired = parsePasswordExpired(err?.response?.data);
      if (status === 403 && expired.hit) {
        throw new PasswordExpiredError(
          expired.message || '密碼已過期，請先更新密碼。',
          expired.rotationDays
        );
      }

      // 401/其他狀態：視為無效 token
      return false;
    }
  }

  async refreshCurrentUser(): Promise<User | null> {
    const token = this.getToken();
    if (!token) return null;

    try {
      const resp = await apiClient.get(`${this.getApiBase()}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const user = resp.data as User;
      this.saveUser(user);
      return user;
    } catch (e: any) {
      const err = e as AxiosError<any>;
      const status = err?.response?.status;

      const expired = parsePasswordExpired(err?.response?.data);
      if (status === 403 && expired.hit) {
        throw new PasswordExpiredError(
          expired.message || '密碼已過期，請先更新密碼。',
          expired.rotationDays
        );
      }

      return null;
    }
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    const token = this.getToken();
    if (!token) throw new Error('尚未登入');

    try {
      await apiClient.post(
        `${this.getApiBase()}/api/auth/change-password`,
        { old_password: oldPassword, new_password: newPassword },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (e: any) {
      const err = e as AxiosError<any>;
      throw new Error(extractErrorMessage(err, '變更密碼失敗'));
    }
  }

  // ✅ 修正點：接受 captchaToken 並傳送至後端
  async forgotPassword(email: string, captchaToken?: string): Promise<void> {
    try {
      await apiClient.post(
        `${this.getApiBase()}/api/auth/forgot-password`,
        {
          email,
          recaptcha_token: captchaToken, // ✅ 傳送 Token 供後端驗證
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
    } catch (e: any) {
      const err = e as AxiosError<any>;
      throw new Error(extractErrorMessage(err, '寄送重設密碼信失敗'));
    }
  }

  async switchCompany(companyId: number): Promise<User> {
    const token = this.getToken();
    const res = await apiClient.post(
      `${this.getApiBase()}/api/auth/switch-company`,
      { company_id: companyId },
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
    );
    const updatedUser: User = res.data.user;
    this.saveUser(updatedUser);
    return updatedUser;
  }
}

export const authService = new AuthService();

function uuidV4FromCrypto(): string {
  if (!globalThis.crypto?.getRandomValues) {
    throw new Error('Secure device_id requires Web Crypto (crypto.getRandomValues).');
  }

  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);

  // UUID v4 規格
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;

  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function getOrCreateDeviceId(): string {
  const key = 'device_id';
  const existing = localStorage.getItem(key);
  if (existing) return existing;

  const id = globalThis.crypto?.randomUUID?.() ?? uuidV4FromCrypto();
  localStorage.setItem(key, id);
  return id;
}
